import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Building2, Eye, Edit, Trash2, CalendarDays, ArrowLeft, MapPin, Users, Bed, Bath, Heart, Tag } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useToast } from "@/hooks/use-toast";
import { propertyService, PropertyService } from "@/services/propertyService";
import { DevelopmentService, Development } from "@/services/developmentService";
import { Property } from "@/integrations/mysql/types";
import { getCoverImageUrl } from "@/lib/imageMap";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";

const OwnerProperties = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userId, loading: authLoading, refreshUser, subscriptionPlan } = useAuthState();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [developmentsMap, setDevelopmentsMap] = useState<Map<number, string>>(new Map());
  const [shareSaleModal, setShareSaleModal] = useState<{
    open: boolean;
    propertyId: number | null;
    propertyTitle: string;
    forSale: boolean;
  }>({ open: false, propertyId: null, propertyTitle: "", forSale: false });
  const [shareSaleLoading, setShareSaleLoading] = useState(false);

  const isShareForSale = (property: Property) => {
    const v = property.is_share_for_sale as boolean | number | undefined;
    return v === true || v === 1;
  };

  const openShareSaleModal = (property: Property, forSale: boolean) => {
    setShareSaleModal({
      open: true,
      propertyId: property.id,
      propertyTitle: property.title,
      forSale,
    });
  };

  const handleConfirmShareSale = async () => {
    if (!shareSaleModal.propertyId) return;
    setShareSaleLoading(true);
    try {
      await PropertyService.setShareForSale(
        shareSaleModal.propertyId,
        shareSaleModal.forSale,
      );
      toast({
        title: shareSaleModal.forSale ? "Cota à venda" : "Venda retirada",
        description: shareSaleModal.forSale
          ? "Seu imóvel foi marcado para venda de cota."
          : "A oferta de venda da cota foi removida.",
      });
      setShareSaleModal((prev) => ({ ...prev, open: false }));
      await loadProperties();
    } catch (error) {
      console.error("Error updating share for sale:", error);
      toast({
        title: "Erro",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível atualizar a venda da cota.",
        variant: "destructive",
      });
    } finally {
      setShareSaleLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('subscription') === 'success') {
      refreshUser?.();
      setSearchParams({}, { replace: true });
      toast({
        title: "Assinatura ativada",
        description: "Seu plano Live foi ativado com sucesso.",
      });
    }
  }, [searchParams, setSearchParams, refreshUser, toast]);

  const loadProperties = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      // Se for admin, carregar todos os imóveis com contagem de favoritos
      // Se for owner, carregar apenas os imóveis do proprietário
      const properties = userType === "admin" 
        ? await PropertyService.getAllPropertiesForAdmin()
        : await PropertyService.getPropertiesByOwner(userId);
      
      // Debug: verificar se favorites_count está presente
      if ((userType === "admin" || userType === "Admin") && properties.length > 0) {
        console.log('🔍 ===== DEBUG ADMIN PROPERTIES =====');
        console.log('🔍 userType:', userType);
        console.log('🔍 Propriedades carregadas (admin):', properties.length);
        console.log('🔍 Primeira propriedade completa:', JSON.stringify(properties[0], null, 2));
        console.log('🔍 favorites_count da primeira:', (properties[0] as any).favorites_count);
        console.log('🔍 Tipo do favorites_count:', typeof (properties[0] as any).favorites_count);
        // Verificar se todas as propriedades têm favorites_count
        properties.forEach((p, index) => {
          const favCount = (p as any).favorites_count;
          console.log(`🔍 Propriedade ${index + 1} (ID: ${p.id}, Título: ${p.title}):`, {
            favorites_count: favCount,
            tipo: typeof favCount,
            convertido: Number(favCount) || 0,
            tem_favorites_count: favCount !== undefined && favCount !== null
          });
        });
        console.log('🔍 ===== FIM DEBUG =====');
      }
      
      setProperties(properties);
    } catch (error) {
      console.error("Error loading properties:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os imóveis.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [userId, userType, toast]);

  useEffect(() => {
    // Aguardar carregamento da autenticação antes de verificar
    // Permitir acesso para owner ou admin
    if (!authLoading && (!isAuthenticated || (userType !== "owner" && userType !== "admin"))) {
      navigate("/login?profile=owner");
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  // Carregar empreendimentos
  useEffect(() => {
    const loadDevelopments = async () => {
      try {
        const allDevelopments = await DevelopmentService.getAllDevelopments();
        const map = new Map<number, string>();
        allDevelopments.forEach(dev => {
          map.set(dev.id, dev.name);
        });
        setDevelopmentsMap(map);
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
      }
    };

    loadDevelopments();
  }, []);

  useEffect(() => {
    if (isAuthenticated && (userType === "owner" || userType === "admin") && userId) {
      loadProperties();
    }
  }, [isAuthenticated, userType, userId, loadProperties]);

  const handleDeleteProperty = async (propertyId: number) => {
    if (!confirm("Tem certeza que deseja excluir este imóvel? Esta ação não pode ser desfeita.")) {
      return;
    }

    try {
      await PropertyService.deleteProperty(propertyId);
      toast({
        title: "Imóvel excluído!",
        description: "O imóvel foi excluído com sucesso.",
      });
      loadProperties();
    } catch (error) {
      console.error("Error deleting property:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o imóvel.",
        variant: "destructive",
      });
    }
  };

  const getPropertyTypeLabel = (type: string) => {
    switch (type) {
      case "apartment":
        return "Apartamento";
      case "house":
        return "Casa";
      case "penthouse":
        return "Cobertura";
      case "cottage":
        return "Chalé";
      default:
        return type;
    }
  };

  const getPropertyTypeIcon = (type: string) => {
    switch (type) {
      case "apartment":
        return "🏢";
      case "house":
        return "🏠";
      case "penthouse":
        return "🏙️";
      case "cottage":
        return "🏡";
      default:
        return "🏠";
    }
  };

  // Mostrar loading enquanto verifica autenticação
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Verificando autenticação...</p>
    </div>
    </div>
    </div>
    );
  }

  return (
    <MinhaAreaPageShell contentClassName="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
      <header className="mb-6 sm:mb-8 space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="gap-2 w-fit -ml-2 px-2"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          Voltar
        </Button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1 space-y-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
              {userType === "admin" ? "Todos os Imóveis" : "Meus Imóveis"}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl">
              {userType === "admin"
                ? "Visualize todos os imóveis cadastrados e suas estatísticas"
                : "Gerencie seus anúncios e visualize estatísticas"}
            </p>
          </div>
          <Button
            onClick={() => navigate("/minha-area/novo-imovel")}
            className="gap-2 w-full sm:w-auto shrink-0"
          >
            <Plus className="h-4 w-4 shrink-0" />
            Novo Imóvel
          </Button>
        </div>
      </header>

        {/* Estatísticas */}
        <div className={`grid grid-cols-2 md:grid-cols-4 ${userType === "admin" ? "lg:grid-cols-5" : ""} gap-3 sm:gap-6 mb-6 sm:mb-8`}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Imóveis</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{properties.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Imóveis Ativos</CardTitle>
              <Eye className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {properties.filter(p => p.is_active).length}
    </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Apartamentos</CardTitle>
              <Building2 className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {properties.filter(p => p.property_type === "apartment").length}
    </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Casas</CardTitle>
              <Building2 className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {properties.filter(p => p.property_type === "house").length}
    </div>
            </CardContent>
          </Card>
          {userType === "admin" && (
            <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20 col-span-2 md:col-span-1 lg:col-span-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Favoritos</CardTitle>
                <Heart className="h-4 w-4 text-red-500 fill-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {properties.reduce((total, p) => total + ((p as any).favorites_count || 0), 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Em todos os imóveis</p>
              </CardContent>
            </Card>
          )}
    </div>

        {/* Lista de Imóveis */}
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
          ) : properties.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhum imóvel encontrado</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Você ainda não possui imóveis cadastrados.
                </p>
                <Button
                  onClick={() => navigate("/minha-area/novo-imovel")}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Cadastrar Primeiro Imóvel
                </Button>
              </CardContent>
            </Card>
          ) : (
            properties.map((property) => (
              <Card key={property.id} className="overflow-hidden">
                <CardHeader className="bg-muted/50 p-4 sm:p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex gap-3 min-w-0 flex-1">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden shrink-0">
                        <img 
                          src={getCoverImageUrl(property)} 
                          alt={property.title}
                          className="w-full h-full object-cover"
                        />
    </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-base sm:text-lg leading-snug break-words">{property.title}</h3>
                        {property.development_id && developmentsMap.has(property.development_id) && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <Building2 className="h-3.5 w-3.5 text-primary" />
                            <span className="text-sm font-medium text-primary truncate">
                              {developmentsMap.get(property.development_id)}
                            </span>
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground">
                          {getPropertyTypeLabel(property.property_type)} • {property.city}, {property.state}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={property.is_active ? "default" : "secondary"}
                      className="w-fit shrink-0 self-start"
                    >
                      {property.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Localização
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Endereço:</strong> {property.address}</p>
                        <p><strong>Cidade:</strong> {property.city}</p>
                        <p><strong>Estado:</strong> {property.state}</p>
                        {property.zip_code && (
                          <p><strong>CEP:</strong> {property.zip_code}</p>
                        )}
    </div>
    </div>

                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Detalhes do Imóvel
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Bed className="h-4 w-4" />
                          <span><strong>{property.bedrooms}</strong> quartos</span>
    </div>
                        <div className="flex items-center gap-2">
                          <Bath className="h-4 w-4" />
                          <span><strong>{property.bathrooms}</strong> banheiros</span>
    </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span><strong>{property.max_guests}</strong> hóspedes</span>
    </div>
                        <p className="text-sm text-muted-foreground"><strong>Preços:</strong> Configure nos períodos de disponibilidade</p>
                      </div>
                    </div>
                  </div>

                  {/* Card de Favoritos */}
                  {(() => {
                    const favCount = Number((property as any).favorites_count) || 0;
                    
                    return (
                      <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border border-blue-400 dark:border-blue-700 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Heart className="h-5 w-5 text-blue-600 fill-blue-600 dark:text-blue-400 dark:fill-blue-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                            <strong className="text-base">{favCount}</strong> {favCount === 1 ? 'favorito' : 'favoritos'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {property.description && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">Descrição</h4>
                      <div 
                        className="text-sm text-muted-foreground line-clamp-3 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: property.description }}
                      />
    </div>
                  )}

                  <div className="mt-6 grid grid-cols-1 min-[480px]:grid-cols-2 gap-2 md:flex md:flex-row md:flex-wrap md:items-center md:gap-2">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/imovel/${property.id}`)}
                        className="gap-2 w-full md:w-auto justify-center h-10 text-sm shrink-0"
                      >
                        <Eye className="h-4 w-4 shrink-0" />
                        Visualizar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/minha-area/editar-imovel/${property.id}`)}
                        className="gap-2 w-full md:w-auto justify-center h-10 text-sm shrink-0"
                      >
                        <Edit className="h-4 w-4 shrink-0" />
                        Editar
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleDeleteProperty(property.id)}
                        className="gap-2 w-full md:w-auto justify-center h-10 text-sm shrink-0 min-[480px]:col-span-2 md:col-span-1"
                      >
                        <Trash2 className="h-4 w-4 shrink-0" />
                        Excluir
                      </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/minha-area/gerenciar-disponibilidade?property=${property.id}`)}
                      className="gap-2 w-full md:w-auto justify-center h-10 text-sm shrink-0 min-[480px]:col-span-2 md:col-span-1"
                    >
                      <CalendarDays className="h-4 w-4 shrink-0" />
                      <span className="truncate">Gerenciar Disponibilidade</span>
                    </Button>
                    {subscriptionPlan === "live" && userType === "owner" && (
                      <Button
                        type="button"
                        onClick={() =>
                          openShareSaleModal(property, !isShareForSale(property))
                        }
                        className={`gap-2 w-full md:w-auto justify-center h-10 text-sm shrink-0 min-[480px]:col-span-2 md:col-span-1 text-white ${
                          isShareForSale(property)
                            ? "bg-orange-600 hover:bg-orange-700"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                      >
                        <Tag className="h-4 w-4 shrink-0" />
                        {isShareForSale(property) ? "Retirar Venda" : "Vender Cota"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

      <ConfirmDialog
        open={shareSaleModal.open}
        onOpenChange={(open) =>
          setShareSaleModal((prev) => ({ ...prev, open }))
        }
        title={
          shareSaleModal.forSale
            ? "Confirmar venda de cota"
            : "Retirar venda de cota"
        }
        description={
          shareSaleModal.forSale
            ? `Deseja marcar "${shareSaleModal.propertyTitle}" como disponível para venda de cota?`
            : `Deseja remover "${shareSaleModal.propertyTitle}" da venda de cota?`
        }
        confirmText={
          shareSaleLoading
            ? "Aguarde..."
            : shareSaleModal.forSale
              ? "Confirmar venda"
              : "Confirmar retirada"
        }
        cancelText="Cancelar"
        onConfirm={() => {
          if (!shareSaleLoading) {
            void handleConfirmShareSale();
          }
        }}
        variant={shareSaleModal.forSale ? "default" : "destructive"}
      />
    </MinhaAreaPageShell>
  );
};

export default OwnerProperties;
